/**
 * Contrato Universal de Simulación Estática — matriz analítica para impresión.
 * Desacopla el motor de impresión del dominio interactivo (sandbox / explora).
 */

export type SimulationLayoutType = 'variable_tracking' | 'case_comparison' | 'data_ledger';

export type SimulationWorkspaceGrid = 'math_5mm' | 'dot_grid' | 'blank';

export interface SimulationMatrixCell {
  value: string;
  isHiddenInput: boolean;
  placeholderLabel?: string;
}

export interface SimulationMatrixRow {
  id: string;
  cells: SimulationMatrixCell[];
}

export interface SimulationMatrixWorkspace {
  gridType: SimulationWorkspaceGrid;
  heightRows: number;
}

export interface SimulationMatrixPayload {
  layoutType: SimulationLayoutType;
  title: string;
  instructionText: string;
  headers: string[];
  rows: SimulationMatrixRow[];
  workspace?: SimulationMatrixWorkspace;
}

function parseCell(raw: unknown, index: number): SimulationMatrixCell {
  if (typeof raw === 'string') {
    return { value: raw.trim(), isHiddenInput: false };
  }
  const cell = raw as Record<string, unknown>;
  const isHiddenInput = cell.isHiddenInput === true;
  return {
    value: String(cell.value ?? '').trim(),
    isHiddenInput,
    placeholderLabel: cell.placeholderLabel != null ? String(cell.placeholderLabel).trim() : undefined,
  };
}

function parseRow(raw: unknown, index: number, columnCount: number): SimulationMatrixRow {
  const row = raw as Record<string, unknown>;
  const id = String(row.id || `row_${index + 1}`);
  let cells: SimulationMatrixCell[] = [];

  if (Array.isArray(row.cells)) {
    cells = row.cells.map((cell, cellIndex) => parseCell(cell, cellIndex));
  }

  while (cells.length < columnCount) {
    cells.push({ value: '', isHiddenInput: true });
  }
  if (cells.length > columnCount) {
    cells = cells.slice(0, columnCount);
  }

  return { id, cells };
}

export function validateSimulationMatrixPayload(raw: unknown): SimulationMatrixPayload {
  if (!raw || typeof raw !== 'object') {
    throw new Error('SimulationMatrix payload must be an object');
  }
  const data = raw as Record<string, unknown>;
  const layoutType = String(data.layoutType || 'case_comparison');
  if (!['variable_tracking', 'case_comparison', 'data_ledger'].includes(layoutType)) {
    throw new Error('SimulationMatrix layoutType invalid');
  }

  const title = String(data.title || 'Simulador').trim();
  const instructionText = String(data.instructionText || data.instruction || '').trim();
  if (!instructionText) {
    throw new Error('SimulationMatrix requires instructionText');
  }

  const headers = Array.isArray(data.headers)
    ? data.headers.map(String).map((h) => h.trim()).filter(Boolean)
    : [];
  if (headers.length < 2) {
    throw new Error('SimulationMatrix requires at least 2 headers');
  }

  const rowsRaw = Array.isArray(data.rows) ? data.rows : [];
  if (rowsRaw.length < 1) {
    throw new Error('SimulationMatrix requires at least 1 row');
  }

  const rows = rowsRaw.map((row, index) => parseRow(row, index, headers.length));

  let workspace: SimulationMatrixWorkspace | undefined;
  if (data.workspace && typeof data.workspace === 'object') {
    const ws = data.workspace as Record<string, unknown>;
    const gridType = String(ws.gridType || 'math_5mm');
    const heightRows = Number(ws.heightRows ?? 4);
    if (!['math_5mm', 'dot_grid', 'blank'].includes(gridType)) {
      throw new Error('SimulationMatrix workspace.gridType invalid');
    }
    if (!Number.isFinite(heightRows) || heightRows < 1 || heightRows > 24) {
      throw new Error('SimulationMatrix workspace.heightRows must be 1–24');
    }
    workspace = {
      gridType: gridType as SimulationWorkspaceGrid,
      heightRows,
    };
  }

  return {
    layoutType: layoutType as SimulationLayoutType,
    title,
    instructionText,
    headers,
    rows,
    workspace,
  };
}

/** Resuelve matriz desde payload sandbox (campo explícito o anidado). */
export function extractSimulationMatrix(payload: Record<string, unknown>): SimulationMatrixPayload | null {
  const candidate = payload.simulationMatrix ?? payload.printMatrix;
  if (!candidate) return null;
  try {
    return validateSimulationMatrixPayload(candidate);
  } catch {
    return null;
  }
}
